import apiService from './apiService';
import { API_ENDPOINTS } from '../config/api';

class AdminService {
  // Admin statisztikák lekérése
  async getAdminStats() {
    try {
      const response = await apiService.get(API_ENDPOINTS.ADMIN.STATS);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      throw error;
    }
  }

  // Függőben lévő szolgáltatók lekérése
  // readiness: 'all' | 'ready' | 'incomplete' — profil-teljesség szerinti szűrés
  async getPendingProviders(page = 1, limit = 10, searchTerm = '', readiness = 'all') {
    try {
      const params = new URLSearchParams({ page, limit, search: searchTerm });
      if (readiness && readiness !== 'all') params.set('readiness', readiness);
      const responseData = await apiService.get(`${API_ENDPOINTS.ADMIN.PROVIDERS_PENDING}?${params.toString()}`);
      return {
        providers: responseData?.providers || [],
        pagination: responseData?.pagination || {}
      };
    } catch (error) {
      console.error('Error fetching pending providers:', error);
      throw error;
    }
  }

  // Jóváhagyott szolgáltatók lekérése
  async getApprovedProviders(page = 1, limit = 10, searchTerm = '') {
    try {
      const params = new URLSearchParams({ page, limit, search: searchTerm });
      const responseData = await apiService.get(`${API_ENDPOINTS.ADMIN.PROVIDERS_APPROVED}?${params.toString()}`);
      return {
        providers: responseData?.providers || [],
        pagination: responseData?.pagination || {}
      };
    } catch (error) {
      console.error('Error fetching approved providers:', error);
      throw error;
    }
  }

  // Elutasított szolgáltatók lekérése
  async getRejectedProviders(page = 1, limit = 10, searchTerm = '') {
    try {
      const params = new URLSearchParams({ page, limit, search: searchTerm });
      const responseData = await apiService.get(`${API_ENDPOINTS.ADMIN.PROVIDERS_REJECTED}?${params.toString()}`);
      return {
        providers: responseData?.providers || [],
        pagination: responseData?.pagination || {}
      };
    } catch (error) {
      console.error('Error fetching rejected providers:', error);
      throw error;
    }
  }

  // ÚJ: Piszkozat (meghívandó) szolgáltatók lekérése
  async getDraftProviders(page = 1, limit = 10, searchTerm = '') {
    try {
      const params = new URLSearchParams({ page, limit, search: searchTerm });
      const responseData = await apiService.get(`${API_ENDPOINTS.ADMIN.PROVIDERS_DRAFT}?${params.toString()}`);
      return {
        providers: responseData?.providers || [],
        pagination: responseData?.pagination || {}
      };
    } catch (error) {
      console.error('Error fetching draft providers:', error);
      throw error;
    }
  }

  // ÚJ: Törölt (inaktivált) szolgáltatók lekérése
  async getDeletedProviders(page = 1, limit = 10, searchTerm = '') {
    try {
      const params = new URLSearchParams({ page, limit, search: searchTerm });
      const responseData = await apiService.get(`${API_ENDPOINTS.ADMIN.PROVIDERS_DELETED}?${params.toString()}`);
      return {
        providers: responseData?.providers || [],
        pagination: responseData?.pagination || {}
      };
    } catch (error) {
      console.error('Error fetching deleted providers:', error);
      throw error;
    }
  }

  // ÚJ: Piszkozat szolgáltató-profil létrehozása admin által
  async createProvider(data) {
    try {
      const response = await apiService.post(API_ENDPOINTS.ADMIN.PROVIDERS_CREATE, data);
      return response;
    } catch (error) {
      console.error('Error creating provider:', error);
      throw error;
    }
  }

  // ÚJ: Meghívó email kiküldése egy piszkozat szolgáltatónak
  async sendProviderInvite(providerId) {
    try {
      const response = await apiService.post(API_ENDPOINTS.ADMIN.PROVIDER_INVITE(providerId));
      return response;
    } catch (error) {
      console.error(`Error sending invite for provider ${providerId}:`, error);
      throw error;
    }
  }

  // Email-sablonok (Email sablonok menü)
  async getEmailTemplates() {
    const response = await apiService.get(API_ENDPOINTS.ADMIN.EMAIL_TEMPLATES);
    return response?.data || { usages: [], templates: [] };
  }

  async saveEmailTemplate(template) {
    const response = template.id
      ? await apiService.put(API_ENDPOINTS.ADMIN.EMAIL_TEMPLATE(template.id), template)
      : await apiService.post(API_ENDPOINTS.ADMIN.EMAIL_TEMPLATES, template);
    return response?.data;
  }

  async setEmailTemplateActive(id, isActive) {
    const response = await apiService.put(API_ENDPOINTS.ADMIN.EMAIL_TEMPLATE(id), { isActive });
    return response?.data;
  }

  async deleteEmailTemplate(id) {
    return apiService.delete(API_ENDPOINTS.ADMIN.EMAIL_TEMPLATE(id));
  }

  async previewEmailTemplate(template) {
    const response = await apiService.post(API_ENDPOINTS.ADMIN.EMAIL_TEMPLATE_PREVIEW, template);
    return response?.data;
  }

  async sendTestEmailTemplate(template, to) {
    return apiService.post(API_ENDPOINTS.ADMIN.EMAIL_TEMPLATE_TEST, { ...template, to });
  }

  // Tömeges meghívó (max 50 id / hívás)
  // templateId: a meghívó ezzel az email sablonnal megy ki (üres = automatikus, az aktív sablonok közül)
  async sendProviderInvitesBulk(providerIds, templateId = null) {
    const response = await apiService.post(API_ENDPOINTS.ADMIN.PROVIDERS_INVITE_BULK, { providerIds, templateId });
    return response?.data || { sent: [], failed: [] };
  }

  // Mai kiküldött meghívók és a napi keret ({ sentToday, limit })
  async getInviteQuota() {
    const response = await apiService.get('/admin/providers/invite-quota');
    return response?.data || { sentToday: 0, limit: 0 };
  }

  // A meghívóhoz választható sablonok (importált meghívó típus)
  async getInviteTemplates() {
    const response = await apiService.get('/admin/providers/invite-templates');
    return response?.data || [];
  }

  // Meghívó előnézete egy szolgáltató adataival (nem küld)
  async previewProviderInvite(templateId, providerId) {
    const response = await apiService.post('/admin/providers/invite-preview', { templateId, providerId });
    return response?.data;
  }

  // Szolgáltató-import: előnézet (dry-run)
  async previewProviderImport(file, minQuality) {
    const response = await apiService.uploadFile(API_ENDPOINTS.ADMIN.PROVIDER_IMPORT_PREVIEW, file, { minQuality });
    return response?.data;
  }

  // Szolgáltató-import indítása (háttérben fut, batchId-t ad vissza)
  async startProviderImport(file, minQuality) {
    const response = await apiService.uploadFile(API_ENDPOINTS.ADMIN.PROVIDER_IMPORTS, file, { minQuality });
    return response?.data;
  }

  async getProviderImports() {
    const response = await apiService.get(API_ENDPOINTS.ADMIN.PROVIDER_IMPORTS);
    return response?.data || [];
  }

  async getProviderImport(id) {
    const response = await apiService.get(API_ENDPOINTS.ADMIN.PROVIDER_IMPORT(id));
    return response?.data;
  }

  async getProviderImportProviders(id, invited = 'all') {
    const response = await apiService.get(`${API_ENDPOINTS.ADMIN.PROVIDER_IMPORT_PROVIDERS(id)}?invited=${invited}`);
    return response?.data || [];
  }

  // Szolgáltató státuszának frissítése (jóváhagyás/elutasítás)
  async updateProviderStatus(providerId, status, adminNotes = '') {
    try {
      const response = await apiService.put(API_ENDPOINTS.ADMIN.PROVIDER_STATUS(providerId), { status, adminNotes });
      return response;
    } catch (error) {
      console.error(`Error updating provider ${providerId} status to ${status}:`, error);
      throw error;
    }
  }

  // ÚJ: Szolgáltató lekérdezése ID alapján (részletes adatokkal)
  async getProviderById(id) {
    try {
      const response = await apiService.get(API_ENDPOINTS.ADMIN.PROVIDER_DETAIL(id)); // Feltételezve, hogy van ilyen endpoint
      return response; // A teljes response objektumot adjuk vissza
    } catch (error) {
      console.error(`Error fetching provider with ID ${id}:`, error);
      throw error;
    }
  }

  // ÚJ: Szolgáltató adatainak frissítése
  async updateProvider(id, providerData) {
    try {
      const response = await apiService.put(API_ENDPOINTS.ADMIN.PROVIDER_DETAIL(id), providerData); // Feltételezve, hogy van ilyen endpoint
      return response; // A teljes response objektumot adjuk vissza
    } catch (error) {
      console.error(`Error updating provider with ID ${id}:`, error);
      throw error;
    }
  }

  // Jelentett tartalmak lekérése (ha mégis szükség lenne rá a jövőben)
  async getReportedContent(page = 1, limit = 10, searchTerm = '') {
    try {
      const params = new URLSearchParams({ page, limit, searchTerm });
      const responseData = await apiService.get(`${API_ENDPOINTS.ADMIN.REPORTED_CONTENT}?${params.toString()}`);
      return {
        reportedItems: responseData?.reportedItems || [],
        pagination: responseData?.pagination || {}
      };
    } catch (error) {
      console.error('Error fetching reported content:', error);
      throw error;
    }
  }

  // Összes felhasználó lekérése (admin)
  async getAllUsers(page = 1, limit = 10, searchTerm = '', sortBy = 'createdAt', sortOrder = 'desc') {
    try {
      const params = new URLSearchParams({ page, limit, search: searchTerm, sortBy, sortOrder });
      const responseData = await apiService.get(`${API_ENDPOINTS.ADMIN.USERS}?${params.toString()}`);
      return {
        users: responseData?.users || [],
        pagination: responseData?.pagination || {}
      };
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  }

  // Felhasználó törlése (admin)
  async deleteUser(userId) {
    try {
      const response = await apiService.delete(API_ENDPOINTS.ADMIN.DELETE_USER(userId));
      return response.data;
    } catch (error) {
      console.error(`Error deleting user ${userId}:`, error);
      throw error;
    }
  }

  // ÚJ: Felhasználó visszaállítása (reaktiválás)
  async reactivateUser(userId) {
    try {
      const response = await apiService.post(API_ENDPOINTS.ADMIN.REACTIVATE_USER(userId));
      return response;
    } catch (error) {
      console.error(`Error reactivating user ${userId}:`, error);
      throw error;
    }
  }

  // ÚJ: Szolgáltató visszaállítása (reaktiválás)
  async reactivateProvider(providerId) {
    try {
      const response = await apiService.post(API_ENDPOINTS.ADMIN.REACTIVATE_PROVIDER(providerId));
      return response;
    } catch (error) {
      console.error(`Error reactivating provider ${providerId}:`, error);
      throw error;
    }
  }

  // Értékelés státuszának frissítése (admin) - ha az admin külön is kezelheti
  async updateReviewStatus(reviewId, status, adminNotes = '') {
    try {
      const response = await apiService.put(API_ENDPOINTS.ADMIN.REVIEW_STATUS(reviewId), { status, adminNotes });
      return response.data;
    } catch (error) {
      console.error(`Error updating review ${reviewId} status to ${status}:`, error);
      throw error;
    }
  }

  // Szolgáltató törlése (admin)
  async deleteProvider(providerId) {
    try {
      const response = await apiService.delete(API_ENDPOINTS.ADMIN.DELETE_PROVIDER(providerId));
      return response.data;
    } catch (error) {
      console.error(`Error deleting provider ${providerId}:`, error);
      throw error;
    }
  }

  // ---- Tömeges email utility ----

  async getBulkEmailTemplates() {
    const response = await apiService.get(API_ENDPOINTS.ADMIN.BULK_EMAIL_TEMPLATES);
    return response?.templates || [];
  }

  async getBulkEmailRecipients() {
    return apiService.get(API_ENDPOINTS.ADMIN.BULK_EMAIL_RECIPIENTS);
  }

  async listBulkEmailRecipients(recipientGroups) {
    const response = await apiService.post(API_ENDPOINTS.ADMIN.BULK_EMAIL_RECIPIENTS_LIST, { recipientGroups });
    return response;
  }

  async previewBulkEmail(templateId, sampleData = {}) {
    const response = await apiService.post(API_ENDPOINTS.ADMIN.BULK_EMAIL_PREVIEW, { templateId, sampleData });
    return response?.html || '';
  }

  async sendTestBulkEmail(templateId, testEmail, sampleData = {}) {
    return apiService.post(API_ENDPOINTS.ADMIN.BULK_EMAIL_TEST, { templateId, testEmail, sampleData });
  }

  async sendBulkEmail(templateId, recipientGroups, recipientOverride) {
    return apiService.post(API_ENDPOINTS.ADMIN.BULK_EMAIL_SEND, {
      templateId,
      ...(recipientOverride ? { recipientOverride } : { recipientGroups }),
    });
  }

  async getEmailLogs({ page = 1, limit = 50, status = '', search = '' } = {}) {
    const params = new URLSearchParams({ page, limit });
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    return apiService.get(`${API_ENDPOINTS.ADMIN.BULK_EMAIL_LOGS}?${params.toString()}`);
  }
}
export default new AdminService();